/* =====================================================================
   SUSU — society order window gate
   ---------------------------------------------------------------------
   Serves BOTH order-window Custom Liquid blocks — the product-page one and
   the collection-page one. They were separate inline scripts; the only real
   difference is what they gate, which the markup now states outright:

     data-susu-scope="product"  gate the buy buttons in this section
     data-susu-scope="grid"     gate the quick-add buttons in the product grid

   The Liquid decides the state server-side and renders it as data-state, so
   the notice is correct with JavaScript off. This script re-evaluates from
   data-opens / data-closes on load and every minute, so a cached page cannot
   show a stale state at the moment a window opens or closes.

   NOTE: this is not enforcement. A direct /cart/add POST bypasses it
   entirely. Genuine enforcement needs a Cart & Checkout Validation Function.
   See docs/order-windows.md.
   ===================================================================== */
(() => {
  'use strict';

  const CART_FORM = 'form[action*="/cart/add"]';

  function currentState(opens, closes) {
    const now = Math.floor(Date.now() / 1000);
    if (opens && now < opens) return 'upcoming';
    if (closes && now > closes) return 'closed';
    return 'open';
  }

  function blockSubmit(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  /* --- which sections does this notice gate? ------------------------- */

  /* Product page: the notice's own section (or the product component). */
  function productSections(notice) {
    const el =
      notice.closest('.shopify-section') ||
      notice.closest('product-component') ||
      document.querySelector('main') ||
      document.body;
    return el ? [el] : [];
  }

  /* Collection page: the section(s) holding the product grid — the ones with
     the most add-to-cart forms. Avoids depending on Horizon's class names,
     and keeps recommended-product rows elsewhere on the page untouched. */
  function gridSections(notice) {
    const own = notice.closest('.shopify-section');

    const counts = [...document.querySelectorAll('main .shopify-section')]
      .filter((section) => section !== own && section.querySelector(CART_FORM))
      .map((section) => ({
        section: section,
        count: section.querySelectorAll(CART_FORM).length
      }));

    if (!counts.length) return [];

    const max = Math.max(...counts.map((entry) => entry.count));

    /* Keep every section tying the largest count — Horizon can split a grid
       across sections on some templates. */
    return counts.filter((entry) => entry.count === max).map((entry) => entry.section);
  }

  /* --- applying the gate --------------------------------------------- */

  function gateSection(section, blocked) {
    section.classList.toggle('susu-ordering-blocked', blocked);

    section.querySelectorAll(CART_FORM).forEach((form) => {
      if (blocked) {
        if (form.dataset.susuBlocked !== 'true') {
          form.addEventListener('submit', blockSubmit, true);
          form.dataset.susuBlocked = 'true';
        }
      } else if (form.dataset.susuBlocked === 'true') {
        form.removeEventListener('submit', blockSubmit, true);
        delete form.dataset.susuBlocked;
      }

      /* Stop keyboard submission too. Only controls THIS script disabled are
         re-enabled, so a genuinely sold-out button stays disabled. */
      form.querySelectorAll('button[type="submit"], [name="add"]').forEach((control) => {
        if (blocked) {
          if (!control.disabled) {
            control.disabled = true;
            control.dataset.susuDisabled = 'true';
          }
        } else if (control.dataset.susuDisabled === 'true') {
          control.disabled = false;
          delete control.dataset.susuDisabled;
        }
      });
    });
  }

  function gateFor(notice, scope, blocked) {
    const sections = scope === 'grid' ? gridSections(notice) : productSections(notice);
    sections.forEach((section) => gateSection(section, blocked));
  }

  function apply() {
    document.querySelectorAll('[data-susu-order-window]').forEach((notice) => {
      const scope = notice.dataset.susuScope === 'grid' ? 'grid' : 'product';

      /* Ambiguous: the product is in several societies with different windows
         and we reached it without collection context, so there is no single
         window to evaluate. There are no dates on this notice, and
         currentState(0, 0) would answer 'open' — so return early rather than
         recompute. Ordering stays blocked until the customer picks a society. */
      if (notice.dataset.susuAmbiguous === 'true') {
        gateFor(notice, scope, true);
        return;
      }

      const opens = parseInt(notice.dataset.opens, 10) || 0;
      const closes = parseInt(notice.dataset.closes, 10) || 0;
      const state = currentState(opens, closes);

      /* The collection block can be configured to show the notice but leave
         quick-add alone. Absent attribute = gate (the product-page default). */
      const gate = notice.dataset.blockQuickAdd !== 'false';
      const blocked = state !== 'open' && gate;

      notice.dataset.state = state;
      notice.className = 'susu-order-window susu-order-window--' + state;

      gateFor(notice, scope, blocked);
    });

    /* The blanket stylesheet stopped quick-add flashing on screen before we
       ran. The precise rule is in place now, so drop it. */
    document.querySelectorAll('[data-susu-blanket]').forEach((el) => el.remove());
  }

  /* --- wiring --------------------------------------------------------- */

  function start() {
    apply();

    /* Horizon re-renders the product form when a variant changes. */
    document.addEventListener('variant:update', apply);
    document.addEventListener('change', (event) => {
      if (event.target.closest && event.target.closest('variant-picker')) {
        setTimeout(apply, 50);
      }
    });

    /* Theme editor section reloads. */
    document.addEventListener('shopify:section:load', apply);

    /* Grids that paginate or filter in place re-render their cards. */
    const main = document.querySelector('main');
    if (main && window.MutationObserver) {
      let queued = false;
      new MutationObserver(() => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; apply(); });
      }).observe(main, { childList: true, subtree: true });
    }

    /* Re-check every minute so a page left open crosses the boundary. */
    setInterval(apply, 60000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
